export interface StudyTechnique {
  id: string;
  title: string;
  category: string;
  summary: string;
  howTo: string[];
  source: string;
  tags: string[];
}

export const studyTechniqueCategories = [
  'Neurociência da Aprendizagem',
  'Alta Evidência Científica',
  'Evidência Moderada',
  'Baixa Evidência (usar com moderação)',
  'Organização e Planejamento',
  'Métodos Clássicos de Leitura',
  'Disciplina, Hábitos e Resiliência',
  'Qualidade de Vida e Desempenho',
  'Performance em Provas',
  'Mentoria e Coaching'
] as const;

const baseStudyTechniques: StudyTechnique[] = [
  // Neurociência da Aprendizagem
  {
    id: 'bdnf-exercicio',
    title: 'BDNF e exercício físico',
    category: 'Neurociência da Aprendizagem',
    summary:
      'O Fator Neurotrófico Derivado do Encéfalo (BDNF) sustenta a plasticidade sináptica e a formação de novos neurônios no hipocampo. O exercício físico, especialmente aeróbico ou intervalado (HIIT), é o gatilho mais potente e confiável para elevar a produção de BDNF, melhorando memória, concentração e aprendizagem espacial.',
    howTo: [
      'Intercalar blocos de estudo com caminhada vigorosa, corrida leve ou treino de força, 3 a 5 vezes por semana.',
      'Priorizar exercício nas horas anteriores a sessões de estudo mais exigentes.',
      'Não tratar o exercício como "tempo perdido": é intervenção bioquímica para sustentar o desempenho cognitivo.'
    ],
    source: 'Pesquisa de neurociência compilada (documento "Estratégias Avançadas de Estudo")',
    tags: ['bdnf', 'exercício', 'neuroplasticidade', 'hipocampo', 'memória']
  },
  {
    id: 'sono-consolidacao',
    title: 'Sono e consolidação da memória',
    category: 'Neurociência da Aprendizagem',
    summary:
      'Durante o sono, especialmente nas fases de ondas lentas, o hipocampo transfere as informações aprendidas durante o dia para o córtex cerebral, consolidando a memória de longo prazo. Dormir menos de 6 horas prejudica esse processo e a estabilidade emocional no dia seguinte.',
    howTo: [
      'Buscar no mínimo 7 horas de sono contínuo por noite, principalmente na véspera de simulados.',
      'Evitar "virar a noite" estudando: o conteúdo revisado sem sono adequado não se consolida.',
      'Manter horário regular de sono para estabilizar o ritmo circadiano de estudo.'
    ],
    source: 'Pesquisa de neurociência compilada (documento "Estratégias Avançadas de Estudo")',
    tags: ['sono', 'memória', 'consolidação', 'descanso']
  },
  {
    id: 'carga-cognitiva',
    title: 'Teoria da Carga Cognitiva (Sweller)',
    category: 'Neurociência da Aprendizagem',
    summary:
      'A memória de trabalho retém apenas 5 a 9 itens por poucos segundos, enquanto a memória de longo prazo é praticamente ilimitada. A carga cognitiva se divide em intrínseca (complexidade do conteúdo), extrínseca (distrações e má organização do material) e germânica (esforço produtivo de aprendizagem real).',
    howTo: [
      'Eliminar distrações e simplificar o material de estudo (reduz carga extrínseca).',
      'Fragmentar conteúdos complexos em partes menores e sequenciais (chunking).',
      'Investir esforço ativo em explicar, testar e aplicar o conteúdo (carga germânica), não só absorver passivamente.'
    ],
    source: 'John Sweller — compilado em "Estratégias Avançadas de Estudo" e "Técnicas de Estudo: Base Científica"',
    tags: ['carga cognitiva', 'memória de trabalho', 'sweller', 'chunking']
  },
  {
    id: 'modo-focado-difuso',
    title: 'Modo focado vs. modo difuso',
    category: 'Neurociência da Aprendizagem',
    summary:
      'O cérebro alterna entre o modo focado (atenção concentrada e direcionada) e o modo difuso (relaxamento que permite conexões criativas). Travar num único modo por tempo demais leva ao "Efeito Einstellung" — insistir numa solução errada. Pausas estratégicas destravam o raciocínio.',
    howTo: [
      'Ao empacar num problema difícil, fazer uma pausa curta (caminhada, banho, devaneio) em vez de forçar a concentração.',
      'Usar blocos de foco intenso de 25 a 50 minutos, seguidos de pausas deliberadas (técnica Pomodoro).',
      'Retornar ao modo focado logo após a pausa para capturar as conexões formadas no modo difuso.'
    ],
    source: 'Barbara Oakley — compilado em "Estratégias Avançadas de Estudo"',
    tags: ['foco', 'modo difuso', 'oakley', 'pomodoro', 'criatividade']
  },
  {
    id: 'chunking-procrastinacao',
    title: 'Chunking e o mecanismo neural da procrastinação',
    category: 'Neurociência da Aprendizagem',
    summary:
      'Chunking é agrupar informações fragmentadas em blocos compactos de significado, liberando espaço na memória de trabalho. A procrastinação, por sua vez, ativa no cérebro os mesmos centros de dor associados ao desconforto físico — mas essa sensação se dissipa nos primeiros minutos de imersão real na tarefa.',
    howTo: [
      'Focar no processo ("estudar 20 minutos concentrado"), não no resultado final doloroso ("terminar o capítulo").',
      'Começar o dia pela disciplina mais difícil, quando a energia e a vontade estão no auge.',
      'Recompensar-se logo após concluir o bloco de foco, para condicionar o hábito com dopamina.'
    ],
    source: 'Pesquisa de neurociência compilada (documento "Estratégias Avançadas de Estudo")',
    tags: ['chunking', 'procrastinação', 'hábito', 'dopamina']
  },

  // Alta Evidência Científica
  {
    id: 'recuperacao-ativa',
    title: 'Recuperação Ativa (Active Recall / Retrieval Practice)',
    category: 'Alta Evidência Científica',
    summary:
      'Fechar o material e forçar o cérebro a evocar a informação de dentro para fora, em vez de reler passivamente. É uma das duas técnicas classificadas como de alta utilidade universal pela metanálise de Dunlosky et al. (2013) e segue confirmada em revisões recentes.',
    howTo: [
      'Fazer questões, flashcards ou tentar reescrever o conteúdo de memória antes de consultar o material.',
      'Explicar o conceito em voz alta sem olhar as anotações.',
      'Sempre conferir o resultado (feedback) logo após tentar recuperar, para corrigir falsas certezas.'
    ],
    source: 'Dunlosky et al. (2013) — compilado nos três documentos de pesquisa e confirmado em revisões recentes',
    tags: ['recuperação ativa', 'active recall', 'testing effect', 'alta evidência']
  },
  {
    id: 'pratica-espacada',
    title: 'Prática Distribuída / Repetição Espaçada',
    category: 'Alta Evidência Científica',
    summary:
      'Revisar o conteúdo em intervalos progressivamente maiores (1 dia, 3 dias, 1 semana, 30 dias...) combate a Curva do Esquecimento de Ebbinghaus, que mostra perda de até 70% da informação não revisada nas primeiras 24 horas. Meta-análises de 2025-2026 confirmam efeito de moderado a alto em matemática (g = 0,28) e em educação médica (SMD = 0,78).',
    howTo: [
      'Programar revisões em intervalos crescentes em vez de estudar tudo de uma vez (evitar prática massificada).',
      'Usar sistemas de repetição espaçada como flashcards digitais (ex.: Anki) para automatizar os intervalos.',
      'Aceitar o esquecimento parcial entre as revisões: o esforço de recuperar de novo é o que fixa a memória.'
    ],
    source: 'Ebbinghaus / Dunlosky et al. (2013); atualizado com meta-análises de 2025 (Educational Psychology Review) e 2026 (The Clinical Teacher)',
    tags: ['repetição espaçada', 'spaced repetition', 'ebbinghaus', 'alta evidência', 'anki']
  },

  // Evidência Moderada
  {
    id: 'interrogacao-elaborativa',
    title: 'Interrogação Elaborativa',
    category: 'Evidência Moderada',
    summary:
      'Consiste em perguntar constantemente "por que este fato é verdadeiro?" diante de afirmações do material de estudo, forçando a integração da nova informação a esquemas já existentes na memória. Apresenta tamanhos de efeito consideráveis (0,85 a 2,57) em avaliações de retenção.',
    howTo: [
      'Ao ler uma afirmação factual, parar e responder por escrito "por que isso é verdade?".',
      'Conectar a resposta a conhecimentos prévios já dominados.',
      'Usar principalmente em conteúdos factuais (datas, definições, dispositivos legais).'
    ],
    source: 'Dunlosky et al. (2013) — compilado em "Estratégias Avançadas de Estudo"',
    tags: ['elaboração', 'interrogação elaborativa', 'evidência moderada']
  },
  {
    id: 'autoexplicacao',
    title: 'Autoexplicação (Self-Explanation)',
    category: 'Evidência Moderada',
    summary:
      'Exige que o estudante narre e justifique internamente cada passo lógico usado para resolver um problema ou aplicar um conceito, tornando visíveis lacunas de raciocínio que passariam despercebidas na leitura passiva.',
    howTo: [
      'Ao resolver uma questão, verbalizar (em voz alta ou por escrito) o porquê de cada passo.',
      'Repetir o processo em problemas variados da mesma matéria.',
      'Usar especialmente em disciplinas exatas e em interpretação de texto.'
    ],
    source: 'Dunlosky et al. (2013) — compilado em "Estratégias Avançadas de Estudo" e "Técnicas de Estudo: Base Científica"',
    tags: ['autoexplicação', 'self-explanation', 'evidência moderada']
  },
  {
    id: 'pratica-intercalada',
    title: 'Prática Intercalada (Interleaving)',
    category: 'Evidência Moderada',
    summary:
      'Alternar o estudo de diferentes matérias ou tipos de problema num mesmo bloco, em vez de esgotar um único tópico (prática massificada). Obriga o cérebro a discriminar qual conceito se aplica a qual situação, preparando melhor para a aleatoriedade real da prova.',
    howTo: [
      'Montar blocos de estudo que alternam disciplinas cognitivamente diferentes (ex.: Raciocínio Lógico com Português).',
      'Evitar sessões de 5+ horas numa única matéria até "perfeição momentânea".',
      'Combinar com o Ciclo de Estudos, que já intercala disciplinas por natureza.'
    ],
    source: 'Dunlosky et al. (2013) — compilado nos três documentos de pesquisa',
    tags: ['intercalação', 'interleaving', 'evidência moderada', 'ciclo de estudos']
  },
  {
    id: 'tecnica-feynman',
    title: 'Técnica de Feynman',
    category: 'Evidência Moderada',
    summary:
      'Baseada no método do físico Richard Feynman: a real medida do domínio sobre um assunto é a capacidade de explicá-lo de forma simples, sem jargões, como se estivesse ensinando a uma criança. Expõe lacunas de compreensão que ficam escondidas atrás de vocabulário técnico.',
    howTo: [
      '1) Escolher um conceito específico e delimitado para dominar.',
      '2) Explicá-lo por escrito ou em voz alta como se fosse para uma criança de 12 anos, sem jargões.',
      '3) Identificar onde a explicação trava ou recorre a termos técnicos vazios — esse é o "buraco de aprendizado".',
      '4) Voltar à fonte, preencher a lacuna e refinar a explicação com analogias simples.'
    ],
    source: 'Richard Feynman — compilado nos três documentos de pesquisa e no "Manual do Concurseiro" (Meta 76)',
    tags: ['feynman', 'ensinar', 'evidência moderada', 'clareza conceitual']
  },
  {
    id: 'codificacao-dupla-mapas',
    title: 'Codificação Dupla e Mapas Mentais',
    category: 'Evidência Moderada',
    summary:
      'Combinar palavras e representações visuais pertinentes (diagramas, esquemas, mapas mentais) fortalece a retenção porque usa dois canais de processamento cerebral simultâneos, desde que a imagem seja funcional e não apenas decorativa.',
    howTo: [
      'Criar mapas mentais que conectem conceitos com setas e hierarquias, não apenas listas.',
      'Combinar texto explicativo curto com diagramas, evitando poluição visual.',
      'Usar principalmente para conteúdos com relações complexas entre conceitos (ex.: processos, fluxos, classificações).'
    ],
    source: '"Técnicas de Estudo: Base Científica" e "Manual do Concurseiro" (Meta 81 — Mapa Mental)',
    tags: ['mapa mental', 'codificação dupla', 'organizadores gráficos']
  },
  {
    id: 'pratica-deliberada',
    title: 'Prática Deliberada',
    category: 'Evidência Moderada',
    summary:
      'Prática focada em pontos específicos de fraqueza, com feedback constante e ajuste contínuo — diferente de simplesmente "fazer muitas questões". Exige metas claras de melhoria a cada sessão.',
    howTo: [
      'Definir uma habilidade específica a melhorar antes de cada sessão (ex.: velocidade em questões de português).',
      'Buscar feedback imediato (gabarito comentado, correção de redação) após cada tentativa.',
      'Aumentar gradualmente a dificuldade conforme a habilidade evolui.'
    ],
    source: '"Técnicas de Estudo: Base Científica"',
    tags: ['prática deliberada', 'feedback', 'evidência moderada']
  },
  {
    id: 'exemplos-resolvidos',
    title: 'Exemplos Resolvidos (Worked Examples)',
    category: 'Evidência Moderada',
    summary:
      'Para iniciantes, estudar o passo a passo de uma resolução especialista reduz a sobrecarga da memória de trabalho e acelera o aprendizado. Para quem já tem proficiência, o excesso de exemplos resolvidos vira redundância e atrapalha (Efeito de Inversão de Perícia) — nessa fase, o ideal é resolver questões inéditas sem apoio.',
    howTo: [
      'Iniciante: estudar resoluções comentadas passo a passo antes de tentar sozinho.',
      'Avançado: reduzir exemplos resolvidos e migrar para baterias de questões inéditas sem consulta.',
      'Reavaliar periodicamente em que estágio de proficiência você está em cada disciplina.'
    ],
    source: 'John Sweller — compilado em "Estratégias Avançadas de Estudo"',
    tags: ['exemplos resolvidos', 'worked examples', 'expertise reversal']
  },

  // Baixa Evidência
  {
    id: 'releitura-grifo',
    title: 'Releitura e grifos (baixa utilidade)',
    category: 'Baixa Evidência (usar com moderação)',
    summary:
      'Reler o mesmo texto ou grifar trechos gera "ilusão de competência": a familiaridade com o texto passa a sensação de domínio, mas sem transferir o conhecimento para a memória de longo prazo de forma testável. Deve ser usado apenas como primeiro contato, nunca como método principal de revisão.',
    howTo: [
      'Usar a primeira leitura só para mapear o conteúdo, não para "decorar por repetição".',
      'Substituir releituras seguintes por recuperação ativa (fechar o livro e tentar responder).',
      'Se for grifar, grifar pouco e revisar os grifos com perguntas, não apenas com o olhar.'
    ],
    source: 'Dunlosky et al. (2013) — compilado nos três documentos de pesquisa',
    tags: ['releitura', 'grifo', 'ilusão de competência', 'baixa evidência']
  },
  {
    id: 'sumarizacao-mnemonicas',
    title: 'Sumarização passiva e mnemônicas de palavras-chave',
    category: 'Baixa Evidência (usar com moderação)',
    summary:
      'Resumir sem processamento crítico costuma degenerar em cópia mecânica, com pouco ganho de retenção. Mnemônicas de palavras-chave ajudam pontualmente (listas, siglas), mas não substituem a compreensão estrutural do conteúdo.',
    howTo: [
      'Se resumir, resumir com as próprias palavras e depois testar-se sobre o resumo.',
      'Usar mnemônicas apenas para itens realmente arbitrários (siglas, ordens, listas curtas).',
      'Não usar mnemônicas como método único para conteúdos conceituais profundos.'
    ],
    source: 'Dunlosky et al. (2013) — compilado nos três documentos de pesquisa',
    tags: ['sumarização', 'mnemônica', 'baixa evidência']
  },

  // Organização e Planejamento
  {
    id: 'ciclo-de-estudos',
    title: 'Ciclo de Estudos',
    category: 'Organização e Planejamento',
    summary:
      'Em vez de fixar matérias a dias da semana, o Ciclo de Estudos organiza um roteiro contínuo de disciplinas com horas proporcionais ao peso de cada uma no edital. Se houver imprevisto, o estudo retoma exatamente de onde parou, sem "perder o calendário".',
    howTo: [
      'Calcular a disponibilidade semanal em horas e definir a duração do ciclo (ex.: 80% da disponibilidade).',
      'Dividir cada disciplina em microblocos de 60 a 120 minutos, nunca em blocos únicos longos.',
      'Intercalar disciplinas cognitivamente diferentes dentro do mesmo ciclo.',
      'Ao interromper por imprevisto, retomar no ponto exato do ciclo, sem tentar recuperar "o dia perdido".'
    ],
    source: 'Alexandre Meirelles — compilado em "Estratégias Avançadas de Estudo" e usado no Meu Plano do app',
    tags: ['ciclo de estudos', 'planejamento', 'cronograma', 'meirelles']
  },
  {
    id: 'caderno-de-erros',
    title: 'Caderno de Erros',
    category: 'Organização e Planejamento',
    summary:
      'Instrumento cirúrgico para transformar erros em simulados em aprendizado estrutural. Não deve copiar a questão inteira, apenas o gatilho exato do erro (a "pegadinha", a exceção, o dispositivo legal ignorado) e a correção sintética.',
    howTo: [
      'Registrar apenas o motivo exato do erro, de forma sintética — nunca a questão inteira.',
      'Indicar o fundamento correto (artigo de lei, súmula, regra gramatical) junto com a anotação.',
      'Revisar o caderno periodicamente e remover itens já dominados, mantendo-o enxuto.'
    ],
    source: 'Compilado em "Estratégias Avançadas de Estudo"; presente também no Manual do Concurseiro',
    tags: ['caderno de erros', 'simulado', 'correção', 'revisão']
  },
  {
    id: 'matriz-eisenhower',
    title: 'Matriz de Eisenhower aplicada ao estudo',
    category: 'Organização e Planejamento',
    summary:
      'Classifica tarefas de estudo por urgência e importância, ajudando a priorizar disciplinas de alto peso no edital e alta dificuldade pessoal, em vez de se perder em tarefas urgentes, mas pouco relevantes para a aprovação.',
    howTo: [
      'Classificar disciplinas/tarefas em: importante+urgente, importante+não urgente, urgente+não importante, nem um nem outro.',
      'Priorizar o quadrante "importante e não urgente" (ex.: revisão espaçada) antes que vire urgência.',
      'Eliminar ou delegar tarefas do quadrante "nem urgente nem importante".'
    ],
    source: '"Técnicas de Estudo: Base Científica"',
    tags: ['eisenhower', 'priorização', 'planejamento']
  },
  {
    id: 'metas-smart',
    title: 'Metas SMART aplicadas ao estudo',
    category: 'Organização e Planejamento',
    summary:
      'Metas específicas, mensuráveis, atingíveis, relevantes e com prazo definido reduzem a vagueza que alimenta a procrastinação (ex.: trocar "estudar mais Direito Constitucional" por "resolver 30 questões de Controle de Constitucionalidade até sexta-feira").',
    howTo: [
      'Definir metas semanais específicas por disciplina, com número de questões, horas ou capítulos.',
      'Revisar as metas todo domingo e ajustar a semana seguinte.',
      'Separar metas de processo (horas estudadas) de metas de resultado (nota em simulado).'
    ],
    source: '"Técnicas de Estudo: Base Científica"',
    tags: ['metas smart', 'planejamento', 'metas de processo']
  },
  {
    id: 'lei-de-pareto',
    title: 'Lei de Pareto nos estudos',
    category: 'Organização e Planejamento',
    summary:
      'Cerca de 20% do conteúdo de um edital costuma gerar 80% dos pontos possíveis na prova. Identificar esses tópicos de maior incidência histórica evita diluir o tempo de estudo igualmente entre assuntos de peso muito diferente.',
    howTo: [
      'Levantar o histórico de incidência de temas em provas anteriores da banca.',
      'Priorizar os 20% de tópicos com maior recorrência e peso.',
      'Reservar tempo residual para os tópicos de menor incidência, sem abandoná-los.'
    ],
    source: 'Manual do Concurseiro (Meta 80)',
    tags: ['pareto', 'priorização', 'peso das disciplinas']
  },
  {
    id: 'edital-esquematizado',
    title: 'Edital Esquematizado, Guia e Sintetizado',
    category: 'Organização e Planejamento',
    summary:
      'Transformar o edital bruto em três ferramentas de acompanhamento: um edital esquematizado (organizado topicamente), um edital guia (com progresso marcado) e um edital sintetizado (resumo dos pontos-chave), trazendo clareza e velocidade ao estudo.',
    howTo: [
      'Reescrever o edital em tópicos e subtópicos organizados, na ordem lógica de estudo.',
      'Marcar o progresso de cada tópico (não iniciado, em andamento, revisado).',
      'Manter uma versão sintetizada com os pontos essenciais de cada tópico já dominado.'
    ],
    source: 'Manual do Concurseiro (Metas 70, 71 e 72)',
    tags: ['edital', 'planejamento', 'acompanhamento']
  },

  // Métodos Clássicos de Leitura
  {
    id: 'sq3r',
    title: 'Método SQ3R',
    category: 'Métodos Clássicos de Leitura',
    summary:
      'Sigla para Survey (pesquisar), Question (perguntar), Read (ler), Recite (recitar) e Review (revisar). Estrutura a leitura de um texto denso em etapas ativas, reduzindo a leitura passiva.',
    howTo: [
      'Survey: passar os olhos pela estrutura do texto (títulos, resumos) antes de ler.',
      'Question: transformar títulos em perguntas a responder durante a leitura.',
      'Read: ler o texto buscando responder às perguntas levantadas.',
      'Recite: fechar o texto e recitar o que foi entendido, com suas próprias palavras.',
      'Review: revisar o conteúdo em intervalos espaçados.'
    ],
    source: 'Manual do Concurseiro (Meta 92)',
    tags: ['sq3r', 'leitura ativa', 'método clássico']
  },
  {
    id: 'ok4r',
    title: 'Método OK4R',
    category: 'Métodos Clássicos de Leitura',
    summary:
      'Variante do SQ3R: Overview (visão geral), Key ideas (ideias-chave), Read, Recite, wRite (escrever) e Review. Adiciona a etapa de escrever o que foi entendido, reforçando a codificação da memória.',
    howTo: [
      'Fazer uma visão geral do material antes de estudar em profundidade.',
      'Identificar as ideias-chave de cada seção antes de ler o texto completo.',
      'Escrever um resumo próprio depois da leitura e revisar posteriormente em espaçamento.'
    ],
    source: 'Manual do Concurseiro (Meta 91)',
    tags: ['ok4r', 'leitura ativa', 'método clássico']
  },
  {
    id: 'fichamento-textos',
    title: 'Fichamento de textos',
    category: 'Métodos Clássicos de Leitura',
    summary:
      'Técnica simples de registrar, em fichas ou notas curtas, os pontos essenciais de um texto lido, facilitando consultas rápidas futuras sem precisar reler o material inteiro.',
    howTo: [
      'Registrar por texto: ideia central, argumentos de apoio e conclusão, em poucas linhas.',
      'Indexar as fichas por tema para consulta rápida antes de simulados.',
      'Evitar transcrever trechos longos — o valor está na síntese, não na cópia.'
    ],
    source: 'Manual do Concurseiro (Meta 79)',
    tags: ['fichamento', 'síntese', 'método clássico']
  },
  {
    id: 'estudar-ensinando',
    title: 'Estudar ensinando (efeito protégé)',
    category: 'Métodos Clássicos de Leitura',
    summary:
      'Explicar o conteúdo para outra pessoa (real ou imaginária) obriga a organizar o raciocínio de forma clara e revela lacunas de compreensão — mecanismo semelhante à Técnica de Feynman.',
    howTo: [
      'Explicar o assunto estudado para um colega, familiar ou em voz alta sozinho.',
      'Responder às dúvidas que a "audiência" levantaria sobre o tema.',
      'Gravar a explicação em áudio e reouvir para identificar pontos confusos.'
    ],
    source: 'Manual do Concurseiro (Meta 76 — Técnica de estudo: ensinar)',
    tags: ['ensinar', 'efeito protégé', 'feynman']
  },
  {
    id: 'estudo-em-grupo',
    title: 'Estudo em grupo',
    category: 'Métodos Clássicos de Leitura',
    summary:
      'Estudar em grupo pode potencializar a recuperação ativa (ao explicar uns para os outros) e a prática intercalada (discutindo questões variadas), mas exige grupos focados e disciplinados para não virar distração.',
    howTo: [
      'Formar grupos pequenos e com objetivo definido por encontro (ex.: revisar 20 questões de um tema).',
      'Alternar quem explica cada tópico, praticando o efeito protégé.',
      'Evitar grupos sem pauta clara, que tendem a virar conversa social.'
    ],
    source: 'Manual do Concurseiro (Meta 78)',
    tags: ['estudo em grupo', 'colaboração']
  },
  {
    id: 'associacoes-mentais',
    title: 'Associações mentais',
    category: 'Métodos Clássicos de Leitura',
    summary:
      'Conectar novos conceitos a imagens, situações ou conhecimentos já dominados facilita a memorização, funcionando como uma forma de elaboração e codificação dupla.',
    howTo: [
      'Ligar conceitos abstratos a situações concretas ou casos práticos conhecidos.',
      'Criar associações pessoais e até bem-humoradas para fixar detalhes difíceis.',
      'Usar com moderação em conteúdos conceituais — não substitui a compreensão estrutural.'
    ],
    source: 'Manual do Concurseiro (Meta 77)',
    tags: ['associação mental', 'memorização']
  },

  // Disciplina, Hábitos e Resiliência
  {
    id: 'ciencia-habitos',
    title: 'Ciência da formação de hábitos aplicada ao estudo',
    category: 'Disciplina, Hábitos e Resiliência',
    summary:
      'Hábitos se formam pelo ciclo gatilho → rotina → recompensa. Depender apenas de motivação (recurso finito e instável) é menos eficaz do que construir rotinas automáticas de estudo ligadas a um gatilho fixo.',
    howTo: [
      'Definir um gatilho fixo para iniciar o estudo (hora do dia, local, ritual de preparação).',
      'Manter a rotina simples e repetível nos primeiros 20-30 dias, até se tornar automática.',
      'Recompensar-se de forma consistente logo após cumprir a rotina.'
    ],
    source: '"Técnicas de Estudo: Base Científica"',
    tags: ['hábito', 'rotina', 'disciplina']
  },
  {
    id: 'growth-mindset',
    title: 'Mentalidade de Crescimento (Growth Mindset)',
    category: 'Disciplina, Hábitos e Resiliência',
    summary:
      'Acreditar que a capacidade intelectual pode ser desenvolvida com esforço (em vez de ser fixa) está associado a maior persistência diante de dificuldades. Atualização importante: meta-análises recentes (Macnamara & Burgoyne, 2023) mostram que o efeito prático de intervenções de growth mindset é menor do que se pensava e muito sensível à qualidade do estudo — a mentalidade ajuda, mas não substitui técnica e prática.',
    howTo: [
      'Interpretar erros como parte do processo de aprendizagem, não como prova de incapacidade.',
      'Focar no esforço e na estratégia usada, não apenas no resultado.',
      'Não tratar "mentalidade" como substituto de técnicas de estudo validadas — combine as duas coisas.'
    ],
    source: 'Compilado em "Estratégias Avançadas de Estudo"; nota crítica atualizada com Macnamara & Burgoyne (2023, PMC) e Burnette et al. (2023)',
    tags: ['growth mindset', 'mentalidade de crescimento', 'resiliência']
  },
  {
    id: 'grit',
    title: 'Grit: paixão e perseverança de longo prazo',
    category: 'Disciplina, Hábitos e Resiliência',
    summary:
      'Combinação de paixão sustentada e perseverança em direção a metas de longo prazo, mesmo diante de reveses. Relevante especialmente em preparações de anos, como concursos de alto nível.',
    howTo: [
      'Reconectar-se periodicamente com o motivo pessoal por trás do objetivo (o "porquê").',
      'Tratar platôs de resultado como parte esperada do processo, não como fracasso.',
      'Medir progresso em janelas longas (meses), não apenas no resultado de cada simulado.'
    ],
    source: '"Técnicas de Estudo: Base Científica"',
    tags: ['grit', 'perseverança', 'resiliência']
  },
  {
    id: 'protocolo-anti-procrastinacao',
    title: 'Protocolo anti-procrastinação',
    category: 'Disciplina, Hábitos e Resiliência',
    summary:
      'A procrastinação é, em grande parte, uma resposta emocional de fuga do desconforto antecipado de uma tarefa — não preguiça ou falta de caráter. Reduzir o tamanho da primeira ação diminui a resistência inicial.',
    howTo: [
      'Reduzir a tarefa à sua menor unidade possível ("abrir o livro na página X e ler 1 parágrafo").',
      'Usar planos "se-então" (ex.: "se são 19h, então estudo Direito Constitucional por 25 minutos").',
      'Começar pela disciplina mais temida no início do dia, quando a energia está mais alta.'
    ],
    source: '"Técnicas de Estudo: Ciência, Coaching e Qualidade de Vida"',
    tags: ['procrastinação', 'planos se-então', 'autorregulação']
  },
  {
    id: 'gestao-ansiedade',
    title: 'Gestão de ansiedade e estresse de desempenho',
    category: 'Disciplina, Hábitos e Resiliência',
    summary:
      'Reinterpretar sintomas físicos de ansiedade (taquicardia, tensão) como preparação fisiológica do corpo para o desafio, em vez de sinal de pânico, reduz o impacto negativo do estresse agudo no desempenho durante a prova.',
    howTo: [
      'Praticar respiração abdominal lenta e profunda antes de simulados e da prova.',
      'Reinterpretar a taquicardia pré-prova como energia mobilizada, não como sinal de fracasso.',
      'Simular condições reais de prova regularmente para reduzir a novidade do estresse no dia D.'
    ],
    source: 'Compilado em "Estratégias Avançadas de Estudo" e "Técnicas de Estudo: Ciência, Coaching e Qualidade de Vida"',
    tags: ['ansiedade', 'estresse', 'regulação emocional']
  },

  // Qualidade de Vida e Desempenho
  {
    id: 'exercicio-fisico-desempenho',
    title: 'Exercício físico e desempenho cognitivo',
    category: 'Qualidade de Vida e Desempenho',
    summary:
      'Além do papel no BDNF, a atividade física regular melhora humor, sono e capacidade de concentração sustentada — todos fatores diretamente ligados ao rendimento nos estudos.',
    howTo: [
      'Reservar ao menos 3 sessões semanais de atividade física, mesmo que curtas.',
      'Preferir horários que não comprometam o sono (evitar treino intenso tarde da noite).',
      'Tratar o exercício como parte do plano de estudos, não como concorrente dele.'
    ],
    source: '"Técnicas de Estudo: Base Científica" e "Estratégias Avançadas de Estudo"',
    tags: ['exercício físico', 'qualidade de vida', 'bdnf']
  },
  {
    id: 'nutricao-hidratacao',
    title: 'Nutrição e hidratação para o cérebro',
    category: 'Qualidade de Vida e Desempenho',
    summary:
      'O cérebro consome cerca de 20% da energia metabólica basal do corpo. Nutrientes precursores de neurotransmissores (ômega-3, complexo B) e hidratação adequada favorecem a propagação eficiente dos impulsos nervosos.',
    howTo: [
      'Manter refeições regulares antes de sessões longas de estudo, evitando picos de glicose.',
      'Hidratar-se ao longo do dia, especialmente em dias de simulado.',
      'Evitar excesso de cafeína e açúcar como única estratégia de energia.'
    ],
    source: 'Compilado em "Estratégias Avançadas de Estudo"',
    tags: ['nutrição', 'hidratação', 'qualidade de vida']
  },
  {
    id: 'pausas-ativas',
    title: 'Pausas ativas e alternância de ambiente',
    category: 'Qualidade de Vida e Desempenho',
    summary:
      'Alternar o ambiente de estudo e incluir pausas ativas (alongamento, caminhada curta) durante sessões longas reduz a fadiga mental e favorece o modo difuso de processamento.',
    howTo: [
      'A cada bloco de foco (ex.: técnica Pomodoro), levantar e se movimentar por alguns minutos.',
      'Variar o local de estudo periodicamente para reduzir a monotonia associativa.',
      'Evitar telas de entretenimento nas pausas — preferir movimento físico ou descanso visual.'
    ],
    source: '"Técnicas de Estudo: Ciência, Coaching e Qualidade de Vida"',
    tags: ['pausas ativas', 'pomodoro', 'ambiente de estudo']
  },

  // Performance em Provas
  {
    id: 'tecnica-pomodoro',
    title: 'Técnica Pomodoro',
    category: 'Performance em Provas',
    summary:
      'Blocos de concentração intensa (tradicionalmente 25 minutos) seguidos de pausas curtas (5 minutos), com uma pausa mais longa a cada 4 blocos. Estrutura o modo focado e evita a fadiga cognitiva.',
    howTo: [
      'Definir um bloco de tempo fixo de estudo ininterrupto (25 a 50 minutos, conforme a disciplina).',
      'Fazer uma pausa curta e verdadeira ao final de cada bloco (sem telefone/redes sociais).',
      'A cada 4 blocos, fazer uma pausa mais longa (15-30 minutos).'
    ],
    source: 'Manual do Concurseiro (Meta 82) e "Estratégias Avançadas de Estudo"',
    tags: ['pomodoro', 'foco', 'gestão do tempo']
  },
  {
    id: 'metodo-star',
    title: 'Método STAR (provas orais e dissertativas)',
    category: 'Performance em Provas',
    summary:
      'Estrutura para organizar respostas em provas orais, dissertativas ou entrevistas: Situação, Tarefa, Ação e Resultado. A etapa de Ação deve ocupar a maior parte da resposta (50-60%), detalhando o raciocínio e as decisões tomadas.',
    howTo: [
      'Situação (10-15%): contextualizar brevemente o cenário do problema.',
      'Tarefa (10-15%): definir claramente o desafio a ser resolvido.',
      'Ação (50-60%): detalhar o raciocínio, análise e medidas adotadas — o núcleo da resposta.',
      'Resultado (15-20%): apresentar o desfecho com dados ou indicadores concretos, quando possível.'
    ],
    source: 'Compilado em "Estratégias Avançadas de Estudo"',
    tags: ['star', 'prova oral', 'prova dissertativa', 'entrevista']
  },
  {
    id: 'estrategias-durante-prova',
    title: 'Estratégias durante a prova objetiva',
    category: 'Performance em Provas',
    summary:
      'Gerenciar o tempo de prova, ler atentamente cada enunciado e ter critério para marcar ou pular questões evita erros por pressa ou má interpretação — fatores que custam pontos independentemente do conteúdo dominado.',
    howTo: [
      'Ler a prova inteira rapidamente antes de começar a responder, identificando questões mais fáceis.',
      'Ler cada pergunta com atenção total antes de responder, evitando "viagem" por familiaridade.',
      'Ao ficar em dúvida entre marcar e revisar, confiar no primeiro julgamento bem fundamentado — mudanças de última hora tendem a ser piores na maioria dos casos.',
      'Reservar os minutos finais só para revisão das questões marcadas como dúvida, não para recomeçar do zero.'
    ],
    source: 'Manual do Concurseiro (Metas 95, 108, 111, 112, 114)',
    tags: ['prova objetiva', 'gestão do tempo de prova', 'estratégia de prova']
  },
  {
    id: 'perfil-da-banca',
    title: 'Conhecer o perfil da banca examinadora',
    category: 'Performance em Provas',
    summary:
      'Analisar o histórico de provas anteriores da banca (estilo de questão, temas recorrentes, nível de detalhe cobrado) direciona o estudo para o que realmente é exigido, complementando a aba "Perfil da Banca" do aplicativo.',
    howTo: [
      'Levantar provas anteriores da mesma banca para o mesmo cargo ou cargos similares.',
      'Mapear os temas mais recorrentes e o estilo de pegadinha característico da banca.',
      'Ajustar o Caderno de Erros e o Ciclo de Estudos com base nesse perfil.'
    ],
    source: 'Manual do Concurseiro (Meta 60)',
    tags: ['perfil da banca', 'histórico de provas', 'planejamento']
  },

  // Mentoria e Coaching
  {
    id: 'modelo-grow',
    title: 'Modelo GROW adaptado ao estudo',
    category: 'Mentoria e Coaching',
    summary:
      'Estrutura de coaching com quatro etapas — Goal (meta), Reality (realidade atual), Options (opções) e Will (compromisso) — usada para planejar mudanças de rota no plano de estudos com clareza.',
    howTo: [
      'Goal: definir claramente a meta de estudo (ex.: nota mínima em simulado até uma data).',
      'Reality: avaliar honestamente a situação atual (horas reais estudadas, resultados obtidos).',
      'Options: listar opções concretas para reduzir a distância entre realidade e meta.',
      'Will: assumir o compromisso com uma ação específica e prazo definido.'
    ],
    source: '"Técnicas de Estudo: Ciência, Coaching e Qualidade de Vida"',
    tags: ['grow', 'coaching', 'mentoria', 'planejamento']
  },
  {
    id: 'woop',
    title: 'WOOP / Contraste Mental',
    category: 'Mentoria e Coaching',
    summary:
      'Técnica de definição de metas que combina visualizar o desejo (Wish), o resultado (Outcome), os obstáculos internos (Obstacle) e um plano de ação para superá-los (Plan) — mais eficaz do que apenas "pensar positivo".',
    howTo: [
      'Wish: definir um desejo de estudo específico e alcançável no curto prazo.',
      'Outcome: visualizar concretamente o melhor resultado de alcançá-lo.',
      'Obstacle: identificar honestamente o principal obstáculo interno (cansaço, distração, medo).',
      'Plan: criar um plano "se-então" para agir diante desse obstáculo quando ele aparecer.'
    ],
    source: '"Técnicas de Estudo: Ciência, Coaching e Qualidade de Vida"',
    tags: ['woop', 'contraste mental', 'metas', 'coaching']
  },
  {
    id: 'mentoria-estrategica',
    title: 'Como escolher e usar um mentor',
    category: 'Mentoria e Coaching',
    summary:
      'Um mentor eficaz para concursos combina experiência prática comprovada com capacidade de dar feedback honesto e específico, funcionando como espelho externo do progresso — reduzindo pontos cegos do candidato.',
    howTo: [
      'Buscar mentores com resultado comprovado na mesma área/cargo pretendido.',
      'Levar dados concretos (desempenho em simulados, Caderno de Erros) aos encontros de mentoria.',
      'Usar a mentoria para calibrar prioridades, não apenas para motivação.'
    ],
    source: '"Técnicas de Estudo: Ciência, Coaching e Qualidade de Vida"',
    tags: ['mentoria', 'feedback', 'coaching']
  }
];

type ManualChapter = { number: number; title: string };

const manualDoConcurseiroChapters: ManualChapter[] = [
  [1, 'A dor e o crescimento humano'], [2, 'Comece o dia sem procrastinar'], [3, 'Arrume sua cama'], [4, 'Planejamento versus tempo'], [5, 'Inteligencia emocional'], [6, 'Autoconhecimento'], [7, 'Arrisque-se'], [8, 'Autoconfianca'], [9, 'Crenças'], [10, 'Seja ensinavel'],
  [11, 'Seja bom'], [12, 'Foco'], [13, 'Os beneficios de estudar'], [14, 'Coragem para decidir'], [15, 'Nao durma no ponto'], [16, 'Nao de atencao a boatos'], [17, 'Efeito Pigmaleao'], [18, 'Como manter a motivacao nos estudos'], [19, 'Apoio da familia'], [20, 'Concurso como objetivo de vida'],
  [21, 'Planeje os estudos antecipadamente'], [22, 'Estude com discricao'], [23, 'Tecnica de imaginacao'], [24, 'Preguica ao estudar'], [25, 'Passos pequenos e progresso'], [26, 'Mural de distracao'], [27, 'Otimismo diante dos obstaculos'], [28, 'Sorte em concursos publicos'], [29, 'Comparacao dos estudos'], [30, 'Medo de nao ser aprovado'],
  [31, 'Treino e prova'], [32, 'Resultados positivos e negativos'], [33, 'Errar sem desistir'], [34, 'Crencas positivas'], [35, 'Efeito sanfona e constancia'], [36, 'Concurseiro protagonista'], [37, 'Estrategia para dificuldades'], [38, 'Erros de concurseiros'], [39, 'Decorar ou memorizar'], [40, 'Como estudar leis e codigos'],
  [41, 'Concentracao'], [42, 'Processo de memorizacao'], [43, 'Cronograma de estudos'], [44, 'Estudo diario e semanal'], [45, 'Aprenda a comemorar'], [46, 'Ferias no estudo'], [47, 'Aprovacao e estrategia'], [48, 'Alimentacao adequada'], [49, 'Durma bem'], [50, 'Exercicio fisico'],
  [51, 'Substancias extras'], [52, 'Concurso e regras do jogo'], [53, 'Tempo de estudo'], [54, 'Com quem estudar'], [55, 'Onde estudar'], [56, 'Melhor horario para estudar'], [57, 'Tempo de prova'], [58, 'Foque em uma area de estudo'], [59, 'Historico do certame'], [60, 'Perfil da banca examinadora'],
  [61, 'Como estudar para mais de um concurso'], [62, 'Material para estudar'], [63, 'Faca provas'], [64, 'Domine o conteudo'], [65, 'Peso das questoes'], [66, 'Cadastro de reserva'], [67, 'Regras restritivas da prova'], [68, 'Filmes motivacionais para estudantes'], [69, 'O concurso pode mudar sua vida'], [70, 'Edital esquematizado'],
  [71, 'Edital guia'], [72, 'Edital sintetizado'], [73, 'Poder da consistencia nos estudos'], [74, 'Dicas de alta performance'], [75, 'Qual e o melhor metodo de estudos'], [76, 'Tecnica de estudo: ensinar'], [77, 'Tecnica de estudo: associacoes mentais'], [78, 'Tecnica de estudo: estude em grupo'], [79, 'Tecnica de estudo: fichamento de textos'], [80, 'Tecnica de estudo: Lei de Pareto'],
  [81, 'Tecnica de estudo: mapa mental'], [82, 'Tecnica de estudo: Pomodoro'], [83, 'Tecnica de estudo: espelhamento'], [84, 'Tecnica de estudo: perguntas'], [85, 'Tecnica de estudo: pensamentos'], [86, 'Tecnica de estudo: sessao de estudo'], [87, 'Tecnica de estudo: descanso e reflexao'], [88, 'Tecnica de estudo: sintonizacao'], [89, 'Tecnica de estudo: imaginacao'], [90, 'Tecnica de estudo: o valor do estudo'],
  [91, 'Tecnica de estudo: OK4R'], [92, 'Tecnica de estudo: SQ3R'], [93, 'Crie suas proprias tecnicas de estudos'], [94, 'Dicas de estudo comprovadas pela ciencia'], [95, 'Prova objetiva'], [96, 'Prova subjetiva'], [97, 'Estrategia para redacao em concursos'], [98, 'Prova de titulos'], [99, 'Prova oral'], [100, 'Prova didatica'],
  [101, 'Prova fisica'], [102, 'Prova pratica'], [103, 'Prova de investigacao social'], [104, 'Recurso'], [105, 'Nao aguento mais estudar'], [106, 'Antes de iniciar a prova'], [107, 'Dia de prova'], [108, 'Aproveite o tempo da prova'], [109, 'Chegue cedo'], [110, 'Estrategias para realizar a prova'],
  [111, 'Marcando as respostas da prova'], [112, 'Leia atentamente as perguntas'], [113, 'Chutando na prova'], [114, 'Rever a questao'], [115, 'O resultado'], [116, 'Detox pos-prova'], [117, 'Seja feliz'], [118, 'Seja melhor do que ontem'], [119, 'Nao ha crescimento sem dor'], [120, 'Lista do estudante']
].map(([number, title]) => ({ number: Number(number), title: String(title) }));

function manualCategory(number: number): (typeof studyTechniqueCategories)[number] {
  if (number <= 38 || number >= 117) return 'Disciplina, H\u00e1bitos e Resili\u00eancia';
  if (number <= 46) return 'Neuroci\u00eancia da Aprendizagem';
  if (number <= 75) return 'Organiza\u00e7\u00e3o e Planejamento';
  if (number <= 94) return 'M\u00e9todos Cl\u00e1ssicos de Leitura';
  return 'Performance em Provas';
}

function manualPractice(number: number, category: string): string[] {
  if (category === 'Performance em Provas') return [
    'Escolha uma prova ou questao relacionada e aplique esta orientacao em um treino cronometrado.',
    'Registre o que funcionou e o principal ponto de melhoria para o proximo simulado.',
    'Transforme o aprendizado em uma regra simples para o dia da prova.'
  ];
  if (category === 'M\u00e9todos Cl\u00e1ssicos de Leitura') return [
    'Aplique a tecnica em um topico real da sua disciplina prioritaraia nesta semana.',
    'Produza uma evidencia curta do estudo: perguntas, resumo, fichamento ou mapa.',
    'Revise o resultado em 24 horas e ajuste o metodo se necessario.'
  ];
  if (category === 'Organiza\u00e7\u00e3o e Planejamento') return [
    'Defina uma proxima acao concreta, com dia, horario e duracao realistas.',
    'Conecte esta acao ao seu Plano de Estudos e acompanhe a execucao.',
    'No fim da semana, confira o que funcionou e ajuste apenas o necessario.'
  ];
  if (category === 'Neuroci\u00eancia da Aprendizagem') return [
    'Experimente a orientacao em um unico bloco de estudo antes de ampliar a rotina.',
    'Observe foco, retencao e energia depois da atividade.',
    'Mantenha o que melhora seu desempenho de forma sustentavel.'
  ];
  return [
    'Escolha uma acao pequena e pratica para hoje, sem esperar o momento perfeito.',
    'Registre seu progresso ao concluir o proximo bloco de estudo.',
    'Use o resultado como referencia para ser melhor do que ontem.'
  ];
}

const manualDoConcurseiroTechniques: StudyTechnique[] = manualDoConcurseiroChapters.map((chapter) => {
  const category = manualCategory(chapter.number);
  return {
    id: `manual-concurseiro-meta-${chapter.number}`,
    title: `Manual do Concurseiro - Meta ${chapter.number}: ${chapter.title}`,
    category,
    summary: `Orientacao do Manual do Concurseiro para fortalecer sua preparacao com clareza e constancia. Use "${chapter.title}" como um convite para transformar a proxima decisao de estudo em acao pratica.`,
    howTo: manualPractice(chapter.number, category),
    source: `Manual do Concurseiro - Capitulo/Meta ${chapter.number}`,
    tags: ['manual do concurseiro', `meta ${chapter.number}`, chapter.title, 'orientacao pratica', 'concurso', 'estudo']
  };
});

const manualDoConcurseiroComplementaryTechniques: StudyTechnique[] = [
  {
    id: 'manual-concurseiro-oracao-dos-concursandos',
    title: 'Ora\u00e7\u00e3o e prepara\u00e7\u00e3o espiritual',
    category: 'Disciplina, H\u00e1bitos e Resili\u00eancia',
    summary: 'A Ora\u00e7\u00e3o dos Concursandos, apresentada no Manual do Concurseiro, convida o estudante a unir f\u00e9, gratid\u00e3o e responsabilidade pessoal. Ela n\u00e3o substitui o estudo planejado: fortalece a serenidade para cumprir cada etapa da prepara\u00e7\u00e3o.',
    howTo: [
      'Antes do primeiro bloco do dia, reserve alguns minutos para ora\u00e7\u00e3o, gratid\u00e3o e defini\u00e7\u00e3o da sua inten\u00e7\u00e3o de estudo.',
      'Conecte a ora\u00e7\u00e3o a uma a\u00e7\u00e3o concreta: abrir o Plano de Estudos e iniciar o pr\u00f3ximo bloco programado.',
      'Antes de simulados ou provas, use esse momento para recuperar a calma e lembrar-se de que prepara\u00e7\u00e3o, foco e const\u00e2ncia caminham juntos.'
    ],
    source: 'Manual do Concurseiro - Ora\u00e7\u00e3o dos Concursandos e Meta 106',
    tags: ['ora\u00e7\u00e3o', 'orar', 'espiritualidade', 'f\u00e9', 'gratid\u00e3o', 'Deus', 'prepara\u00e7\u00e3o espiritual', 'concurseiro']
  }
];

export const studyTechniques: StudyTechnique[] = [
  ...baseStudyTechniques,
  ...manualDoConcurseiroTechniques,
  ...manualDoConcurseiroComplementaryTechniques
];
